/**
 * Keyboard Shortcuts Provider Component - Task 29: Performance Optimization and Advanced Features
 * Provider component that manages keyboard shortcuts context
 */

import React, { useState, useEffect, useCallback } from 'react';

import KeyboardShortcutsHelp from './KeyboardShortcutsHelp';

// Shortcut definitions
export interface KeyboardShortcut {
  /** The key to match - can be event.key value or event.code for bilingual support */
  key: string;
  /** Use event.code instead of event.key for physical key position (bilingual support) */
  useCode?: boolean;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  meta?: boolean;
  description: string;
  action: () => void;
  category: 'navigation' | 'actions' | 'global';
  /** Disable input guard - allow shortcut even when focused on input */
  allowInInput?: boolean;
}

interface KeyboardShortcutsProviderProps {
  children: React.ReactNode;
}

interface KeyboardShortcutsActionsContextType {
  registerShortcut: (shortcut: KeyboardShortcut) => () => void;
  unregisterShortcut: (key: string) => void;
  showHelp: () => void;
}

const KeyboardShortcutsActionsContext =
  React.createContext<KeyboardShortcutsActionsContextType | null>(null);
const KeyboardShortcutsStateContext = React.createContext<KeyboardShortcut[]>(
  []
);

// Global shortcuts registry
class KeyboardShortcutsManager {
  private shortcuts = new Map<string, KeyboardShortcut>();
  private listeners = new Set<(shortcuts: KeyboardShortcut[]) => void>();
  private notifyTimeout: NodeJS.Timeout | null = null;

  register(shortcut: KeyboardShortcut): () => void {
    const key = this.getShortcutKey(shortcut);
    this.shortcuts.set(key, shortcut);
    this.scheduleNotify();
    return () => this.unregister(key);
  }

  unregister(key: string): void {
    if (this.shortcuts.has(key)) {
      this.shortcuts.delete(key);
      this.scheduleNotify();
    }
  }

  private scheduleNotify(): void {
    if (this.notifyTimeout) return;

    this.notifyTimeout = setTimeout(() => {
      this.notifyTimeout = null;
      this.notifyListeners();
    }, 0);
  }

  private notifyListeners(): void {
    const shortcuts = this.getAllShortcuts();
    this.listeners.forEach(listener => {
      try {
        listener(shortcuts);
      } catch (error) {
        console.error('Error in keyboard shortcut listener:', error);
      }
    });
  }

  getAllShortcuts(): KeyboardShortcut[] {
    return Array.from(this.shortcuts.values());
  }

  handleKeyDown(event: KeyboardEvent): boolean {
    // Input guard: Skip shortcuts when user is typing in an input field
    const target = event.target as HTMLElement;
    const isInputFocused = this.isInputElement(target);

    const shortcut = this.findMatchingShortcut(event, isInputFocused);
    if (shortcut) {
      event.preventDefault();
      event.stopPropagation();
      shortcut.action();
      return true;
    }
    return false;
  }

  /**
   * Check if the target element is an input element
   * Used for input guard to prevent shortcuts while typing
   */
  private isInputElement(element: HTMLElement): boolean {
    const tagName = element.tagName.toLowerCase();
    const isInput =
      tagName === 'input' || tagName === 'textarea' || tagName === 'select';
    const isContentEditable = element.isContentEditable;
    return isInput || isContentEditable;
  }

  private findMatchingShortcut(
    event: KeyboardEvent,
    isInputFocused: boolean
  ): KeyboardShortcut | undefined {
    const modifiers = {
      ctrl: event.ctrlKey,
      shift: event.shiftKey,
      alt: event.altKey,
      meta: event.metaKey,
    };

    for (const shortcut of this.shortcuts.values()) {
      // Skip shortcuts that don't allow input if user is typing
      if (isInputFocused && !shortcut.allowInInput) {
        // Exception: Always allow Escape to close things
        if (
          shortcut.key.toLowerCase() !== 'escape' &&
          shortcut.key !== 'Escape'
        ) {
          continue;
        }
      }

      // Match using event.code for bilingual support, or event.key for standard keys
      const eventKey = shortcut.useCode
        ? this.normalizeCode(event.code)
        : this.normalizeKey(event.key);
      const shortcutKey = shortcut.useCode
        ? this.normalizeCode(shortcut.key)
        : this.normalizeKey(shortcut.key);

      if (
        shortcutKey === eventKey &&
        !!shortcut.ctrl === modifiers.ctrl &&
        !!shortcut.shift === modifiers.shift &&
        !!shortcut.alt === modifiers.alt &&
        !!shortcut.meta === modifiers.meta
      ) {
        return shortcut;
      }
    }
    return undefined;
  }

  /**
   * Normalize event.code for physical key position matching
   * This enables bilingual keyboard support (e.g., Thai/English)
   */
  private normalizeCode(code: string | undefined): string {
    if (!code) return '';
    // Map common codes to simpler names
    const codeMap: Record<string, string> = {
      keya: 'a',
      keyb: 'b',
      keyc: 'c',
      keyd: 'd',
      keye: 'e',
      keyf: 'f',
      keyg: 'g',
      keyh: 'h',
      keyi: 'i',
      keyj: 'j',
      keyk: 'k',
      keyl: 'l',
      keym: 'm',
      keyn: 'n',
      keyo: 'o',
      keyp: 'p',
      keyq: 'q',
      keyr: 'r',
      keys: 's',
      keyt: 't',
      keyu: 'u',
      keyv: 'v',
      keyw: 'w',
      keyx: 'x',
      keyy: 'y',
      keyz: 'z',
      digit0: '0',
      digit1: '1',
      digit2: '2',
      digit3: '3',
      digit4: '4',
      digit5: '5',
      digit6: '6',
      digit7: '7',
      digit8: '8',
      digit9: '9',
      slash: '/',
      backslash: '\\',
      period: '.',
      comma: ',',
      semicolon: ';',
      quote: "'",
      bracketleft: '[',
      bracketright: ']',
      minus: '-',
      equal: '=',
      backquote: '`',
      space: 'space',
      enter: 'enter',
      escape: 'escape',
      arrowup: 'up',
      arrowdown: 'down',
      arrowleft: 'left',
      arrowright: 'right',
      tab: 'tab',
      backspace: 'backspace',
      delete: 'delete',
    };
    return codeMap[code.toLowerCase()] || code.toLowerCase();
  }

  private getShortcutKey(shortcut: KeyboardShortcut): string {
    return `${shortcut.ctrl ? 'ctrl+' : ''}${shortcut.shift ? 'shift+' : ''}${shortcut.alt ? 'alt+' : ''}${shortcut.meta ? 'meta+' : ''}${shortcut.key}`;
  }

  private normalizeKey(key: string | undefined): string {
    if (!key) return '';
    const keyMap: Record<string, string> = {
      ' ': 'space',
      arrowup: 'up',
      arrowdown: 'down',
      arrowleft: 'left',
      arrowright: 'right',
      escape: 'esc',
    };
    return keyMap[key.toLowerCase()] || key.toLowerCase();
  }

  subscribe(listener: (shortcuts: KeyboardShortcut[]) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

const shortcutsManager = new KeyboardShortcutsManager();

export const KeyboardShortcutsProvider: React.FC<
  KeyboardShortcutsProviderProps
> = ({ children }) => {
  const [shortcuts, setShortcuts] = useState<KeyboardShortcut[]>([]);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const unsubscribe = shortcutsManager.subscribe(setShortcuts);
    return unsubscribe;
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Handle help shortcut
      if (event.key === '?' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        setShowHelp(true);
        return;
      }
      shortcutsManager.handleKeyDown(event);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const registerShortcut = useCallback((shortcut: KeyboardShortcut) => {
    return shortcutsManager.register(shortcut);
  }, []);

  const unregisterShortcut = useCallback((key: string) => {
    shortcutsManager.unregister(key);
  }, []);

  const showHelpModal = useCallback(() => {
    setShowHelp(true);
  }, []);

  const actionsValue = React.useMemo(
    () => ({
      registerShortcut,
      unregisterShortcut,
      showHelp: showHelpModal,
    }),
    [registerShortcut, unregisterShortcut, showHelpModal]
  );

  return (
    <KeyboardShortcutsActionsContext.Provider value={actionsValue}>
      <KeyboardShortcutsStateContext.Provider value={shortcuts}>
        {children}
        <KeyboardShortcutsHelp
          shortcuts={shortcuts}
          isOpen={showHelp}
          onClose={() => setShowHelp(false)}
        />
      </KeyboardShortcutsStateContext.Provider>
    </KeyboardShortcutsActionsContext.Provider>
  );
};

export const useKeyboardShortcuts = () => {
  const context = React.useContext(KeyboardShortcutsActionsContext);
  if (!context) {
    throw new Error(
      'useKeyboardShortcuts must be used within KeyboardShortcutsProvider'
    );
  }
  return context;
};

export const useKeyboardShortcutsState = () => {
  return React.useContext(KeyboardShortcutsStateContext);
};

export default KeyboardShortcutsProvider;
